/**
 * utils/riskUtils.js
 * Risk scoring, alert fatigue, IR checklist, firewall rules
 */
import { severityFromLabel } from "../config/severity.js";
import { SEVERITY_COLORS } from "../config/constants.js";
import { getPortInfo } from "../config/ports.js";

export { estimateMTTD, calculateAlertFatigue } from "./formatUtils";

// ── Incident Response Checklist ───────────────────────────────────────────────

export const IR_CHECKLIST = {
  critical: [
    "Isolate affected systems from network immediately",
    "Preserve system memory and disk images for forensics",
    "Notify CISO and incident response team",
    "Activate Business Continuity Plan",
    "Preserve all logs — do not reboot affected systems",
    "Identify patient zero — first infected/compromised host",
    "Block attacker IPs at perimeter firewall",
    "Reset all privileged account credentials",
    "Notify legal and compliance teams",
    "Document timeline of events with timestamps",
  ],
  high: [
    "Increase monitoring on affected systems",
    "Block suspicious source IPs at firewall",
    "Review and rotate affected credentials",
    "Notify security team and management",
    "Preserve relevant logs for 90 days",
    "Conduct vulnerability assessment on targeted systems",
    "Review access logs for lateral movement",
  ],
  medium: [
    "Log and monitor the activity",
    "Review firewall rules for affected ports",
    "Check for signs of persistence mechanisms",
    "Update IDS/IPS signatures",
    "Schedule vulnerability scan",
  ],
  low: [
    "Document the event",
    "Monitor for escalation",
    "Review during next security assessment",
  ],
};

// ── Firewall Rule Templates ───────────────────────────────────────────────────

export const generateFirewallRules = (topIPs, topPorts, attackTypes) => {
  const rules = [];

  topIPs?.slice(0, 5).forEach((ip, i) => {
    rules.push({
      id:       `FW-${String(i + 1).padStart(3, "0")}`,
      action:   "DENY",
      direction:"INBOUND",
      src:      ip.name,
      dst:      "ANY",
      port:     "ANY",
      protocol: "ANY",
      reason:   `Top attacker IP — ${ip.value} events detected`,
    });
  });

  topPorts?.slice(0, 5).forEach((port, i) => {
    const info = getPortInfo(port.name);
    if (info.risk === "critical" || info.risk === "high") {
      rules.push({
        id:       `FW-${String(topIPs?.length + i + 1).padStart(3, "0")}`,
        action:   "RESTRICT",
        direction:"INBOUND",
        src:      "EXTERNAL",
        dst:      "ANY",
        port:     port.name,
        protocol: "TCP",
        reason:   `High-risk port — ${info.service} (${info.app})`,
      });
    }
  });

  return rules;
};

// ── Attack Type → Reporting Source Map ───────────────────────────────────────


export const calculateRiskScore = (stats, mlResult) => {
  if (!stats) return 0;

  // ── D1: Base score from highest severity class PRESENT ────────────────────
  // Even 1 confirmed critical attack = base 60 — presence matters, not just count
  let score = 0;
  if      (stats.critical > 0) score = 60;
  else if (stats.high     > 0) score = 40;
  else if (stats.medium   > 0) score = 20;
  else if (stats.low      > 0) score = 10;
  else return 0; // all benign — no risk

  // ── D2: Volume amplifier (+2 to +20) ─────────────────────────────────────
  const attackCount = stats.critical + stats.high + stats.medium + stats.low;
  let volBonus = 0;
  if      (attackCount >= 100000) volBonus = 20;
  else if (attackCount >= 10000)  volBonus = 18;
  else if (attackCount >= 1000)   volBonus = 15;
  else if (attackCount >= 100)    volBonus = 10;
  else if (attackCount >= 10)     volBonus = 5;
  else                            volBonus = 2; // 1–9 confirmed attacks still matters
  score += volBonus;

  // ── D3: Attack diversity bonus (+0 to +10) ────────────────────────────────
  const nonBenignTypes = (stats.attackDist || []).filter(a =>
    !['benign','normal','legitimate'].some(x => a.name.toLowerCase().includes(x))
  ).length;
  score += Math.min(nonBenignTypes * 3, 10);

  // ── D4: ML anomaly confirmation (+0 to +5) ────────────────────────────────
  if (mlResult?.anomaly_rate) {
    score += Math.min(mlResult.anomaly_rate * 0.05, 5);
  }

  return Math.min(Math.round(score), 100);
};

export const getRiskScoreBreakdown = (stats, mlResult) => {
  if (!stats) return null;
  const attackCount = stats.critical + stats.high + stats.medium + stats.low;
  let base = 0;
  if      (stats.critical > 0) base = 60;
  else if (stats.high     > 0) base = 40;
  else if (stats.medium   > 0) base = 20;
  else if (stats.low      > 0) base = 10;
  else return null;

  let vol = 0;
  if      (attackCount >= 100000) vol = 20;
  else if (attackCount >= 10000)  vol = 18;
  else if (attackCount >= 1000)   vol = 15;
  else if (attackCount >= 100)    vol = 10;
  else if (attackCount >= 10)     vol = 5;
  else                            vol = 2;

  const nonBenignTypes = (stats.attackDist || []).filter(a =>
    !['benign','normal','legitimate'].some(x => a.name.toLowerCase().includes(x))
  ).length;
  const div = Math.min(nonBenignTypes * 3, 10);
  const ml  = mlResult?.anomaly_rate ? Math.min(mlResult.anomaly_rate * 0.05, 5) : 0;

  return { base, vol, div, ml, total: Math.min(Math.round(base+vol+div+ml), 100) };
};

export const getRiskLabel = (score) => {
  if (score >= 75) return { label: "CRITICAL RISK", color: SEVERITY_COLORS.critical };
  if (score >= 50) return { label: "HIGH RISK",     color: SEVERITY_COLORS.high     };
  if (score >= 25) return { label: "MEDIUM RISK",   color: SEVERITY_COLORS.medium   };
  return                   { label: "LOW RISK",      color: SEVERITY_COLORS.low      };
};
